import React, { useEffect, useMemo, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { Box, Grid, Paper, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Bar } from 'react-chartjs-2';
import { getPeriodReporting } from '../api';
import PageHeader from './common/PageHeader';
import ChartCard from './common/ChartCard';
import { exportChartsPdf } from '../utils/exportPdf';

function createBarData(rows, field, primaryColor) {
  const labels = rows.map(r => {
    const d = dayjs(r.start_time);
    return `ID ${r.periodId} (${d.isValid() ? d.format('MM/DD') : '?'})`;
  });
  const data = rows.map(r => Number(r[field] || 0));
  return {
    labels,
    datasets: [{ label: field, data, backgroundColor: primaryColor }]
  };
}

const opts = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
};

export default function PeriodReporting() {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const chartsRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getPeriodReporting();
        setRows(res?.rows || []);
      } catch {
        setError('Failed to load Period reporting');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Memoize chart data - must be called before any conditional returns
  const primaryColor = theme.palette.primary.main;
  const completed = useMemo(() => createBarData(rows, 'completedProfiles', primaryColor), [rows, primaryColor]);
  const active    = useMemo(() => createBarData(rows, 'uniqueActiveUsers', primaryColor), [rows, primaryColor]);
  const claims    = useMemo(() => createBarData(rows, 'uniqueDailyClaims', primaryColor), [rows, primaryColor]);
  const bidders   = useMemo(() => createBarData(rows, 'uniqueBidders', primaryColor), [rows, primaryColor]);
  const recipients= useMemo(() => createBarData(rows, 'uniqueRecipients', primaryColor), [rows, primaryColor]);
  const accepted  = useMemo(() => createBarData(rows, 'acceptedConnections', primaryColor), [rows, primaryColor]);
  const rewarded  = useMemo(() => createBarData(rows, 'rewardedConnections', primaryColor), [rows, primaryColor]);

  if (error) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }
  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading…</Typography>
      </Paper>
    );
  }

  // Build a window subtitle (first period → last period)
  const windowSubtitle = rows?.length
    ? `${dayjs(rows[0].start_time).format('MMM D, YYYY')} - ${dayjs(rows[rows.length - 1].end_time).format('MMM D, YYYY')}`
    : '';

  const handleDownloadPdf = async () => {
    await exportChartsPdf({
      rootEl: chartsRef.current,
      fileName: `period-reporting_${dayjs().format('YYYY-MM-DD_HH-mm')}.pdf`,
      headerTitle: 'Bidsy reporting by bidding period',
      headerSubtitle: windowSubtitle || 'All periods',
      logoUrl: '/bidsy-logo.png'
    });
  };

  return (
    <Box>
      <PageHeader
        title="Bidsy reporting by bidding period"
        subtitle="One bar per bidding period"
        actions={<Button variant="contained" onClick={handleDownloadPdf}>Download PDF</Button>}
      />
      <Grid container spacing={2} ref={chartsRef}>
        <Grid item xs={12}>
          <ChartCard title="Completed Profiles Added" height={360}>
            <Bar data={completed} options={opts} />
          </ChartCard>
        </Grid>
        <Grid item xs={12}>
          <ChartCard title="Unique Active Users (sessions)" height={360}>
            <Bar data={active} options={opts} />
          </ChartCard>
        </Grid>
        <Grid item xs={12}>
          <ChartCard title="Unique Daily Claims" height={360}>
            <Bar data={claims} options={opts} />
          </ChartCard>
        </Grid>
        <Grid item xs={12}>
          <ChartCard title="Unique Users Placing Bids" height={360}>
            <Bar data={bidders} options={opts} />
          </ChartCard>
        </Grid>
        <Grid item xs={12}>
          <ChartCard title="Unique Users Receiving Bids" height={360}>
            <Bar data={recipients} options={opts} />
          </ChartCard>
        </Grid>
        <Grid item xs={12}>
          <ChartCard title="Accepted connections" height={360}>
            <Bar data={accepted} options={opts} />
          </ChartCard>
        </Grid>
        <Grid item xs={12}>
          <ChartCard title="Accepted & rewarded connections" height={360}>
            <Bar data={rewarded} options={opts} />
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
}

